import React, { useEffect, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { X, Lightning, Sparkle, CircleNotch } from "@phosphor-icons/react";
import { http, publicUrl } from "../lib/api";
import { addPendingPayment, openCheckoutUrl } from "../lib/checkout";

const PACKAGE_META = {
  standard_35: {
    icon: Lightning,
    title: "Standard-Paket",
    features: ["35 Standard-Anfragen", "Alle Sensoren nutzbar", "Physik- & Mathe-Chat", "Nur Texteingabe"],
  },
  pro_10: {
    icon: Sparkle,
    title: "Pro-Paket",
    features: ["10 Pro-Anfragen", "Leistungsstärkeres KI-Modell", "Bilder & Dateien anhängen (max. 4 x 10 MB)", "Bildanalyse & Dateninterpretation"],
    highlight: true,
  },
};

const waitForNextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

export default function Shop({ open, hint, deviceId, credits, onClose, showToast }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!open) return;
    http
      .get("/packages")
      .then((r) => setPackages(r.data))
      .catch((e) => console.warn("Pakete laden fehlgeschlagen:", e));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setLoading(null);
      return undefined;
    }

    const resetCheckoutUi = () => setLoading(null);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetCheckoutUi();
      }
    };

    window.addEventListener("pageshow", resetCheckoutUi);
    window.addEventListener("focus", resetCheckoutUi);
    document.addEventListener("visibilitychange", onVisibilityChange);
    let browserFinishedHandle;
    let appStateHandle;

    if (Capacitor.isNativePlatform()) {
      Browser.addListener("browserFinished", resetCheckoutUi).then((handle) => {
        browserFinishedHandle = handle;
      });
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) resetCheckoutUi();
      }).then((handle) => {
        appStateHandle = handle;
      });
    }

    return () => {
      window.removeEventListener("pageshow", resetCheckoutUi);
      window.removeEventListener("focus", resetCheckoutUi);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (browserFinishedHandle) browserFinishedHandle.remove();
      if (appStateHandle) appStateHandle.remove();
    };
  }, [open]);

  if (!open) return null;

  const buy = async (packageId) => {
    setLoading(packageId);
    try {
      const { data } = await http.post("/payments/checkout", {
        device_id: deviceId,
        package_id: packageId,
        origin_url: publicUrl("/"),
      });
      addPendingPayment(data.session_id);
      setLoading(null);
      await openCheckoutUrl(data.url, {
        isNativePlatform: () => Capacitor.isNativePlatform(),
        openNativeBrowser: (url) => Browser.open({ url }),
        beforeOpen: () => setLoading(null),
        waitForCheckoutHandoff: waitForNextFrame,
      });
    } catch (e) {
      showToast("Checkout konnte nicht gestartet werden.", "error");
      setLoading(null);
    }
  };

  const cancel = () => {
    setLoading(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-xl flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        data-testid="shop-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-bg border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[90dvh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-bg">
          <h2 className="font-heading text-xl font-bold tracking-tight">ANFRAGEN KAUFEN</h2>
          <button data-testid="shop-close-btn" onClick={cancel} className="p-2 rounded-full bg-elevated border border-white/10" aria-label="Schließen">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {hint && (
            <div className="bg-volt/10 border border-volt/40 rounded-xl px-4 py-3 text-sm text-volthover">{hint}</div>
          )}
          {credits && (
            <p className="text-sm text-muted">
              Dein Guthaben: <strong className="text-white">{credits.standard} Standard</strong>{" "}
              <strong className="text-white">{credits.pro} Pro</strong>
            </p>
          )}
          {packages.map((p) => {
            const meta = PACKAGE_META[p.id] || {};
            const Icon = meta.icon || Lightning;
            return (
              <div
                key={p.id}
                data-testid="purchase-card"
                className={`rounded-xl border p-5 ${meta.highlight ? "border-volt bg-volt/5" : "border-white/10 bg-surface"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon size={22} weight="duotone" className="text-volt" />
                    <span className="font-heading text-lg font-bold">{meta.title || p.label}</span>
                  </div>
                  <span className="font-heading text-2xl font-black text-volt">{`${p.amount.toFixed(2).replace(".", ",")} EUR`}</span>
                </div>
                <ul className="text-sm text-muted space-y-1 mb-4">
                  {(meta.features || [p.label]).map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-volt" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`buy-${p.id}`}
                  onClick={() => buy(p.id)}
                  disabled={loading !== null}
                  className={`w-full rounded-xl py-3.5 font-bold text-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 ${
                    meta.highlight ? "bg-volt hover:bg-volthover" : "bg-elevated border border-white/15 hover:border-volt/60"
                  } disabled:opacity-50`}
                >
                  {loading === p.id && <CircleNotch size={16} className="animate-spin" />}
                  Jetzt kaufen
                </button>
              </div>
            );
          })}
          <p className="text-[11px] text-muted text-center pb-1">
            Zahlungen werden sicher über Stripe abgewickelt. Dein Guthaben wird nach der Bestätigung automatisch gutgeschrieben.
          </p>
          <button
            type="button"
            onClick={cancel}
            className="w-full rounded-xl py-3.5 font-bold text-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 bg-elevated border border-white/15 hover:border-white/30"
          >
            <X size={16} weight="bold" />
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
